import { useFFRestApi } from '@/composables/useApi';
import { InternalError } from '../errors/internal.error';
import { ApiRequestFatalError } from '../errors/api-request-fatal.error';
import { ApiRequestServerError } from '../errors/api-request-server.error';
import { authExchangeFetch } from './auth-exchange.client';
import { useAuthenticationStore } from './authentication.store';
import { HttpStatus } from '../errors/enums/http-status.enum';

export type RefreshTokenBundle = {
  token: string;
  refreshToken: string;
  /** Unix epoch in **milliseconds** (compared directly against `Date.now()`). */
  refreshTokenExpiresAt: number;
};

export class AuthenticationPersistence {
  private readonly store = useAuthenticationStore();

  public async refreshIdTokenLegacy(): Promise<{
    token: string;
    refreshToken?: string;
    refreshTokenExpiresAt?: number;
  }> {
    const result = await authExchangeFetch<{
      token: string;
      refreshToken?: string;
      refreshTokenExpiresAt?: number;
    }>({
      method: 'GET',
      path: '/auth/refresh',
      bearerToken: this.store.idToken,
    });

    if (result.status === HttpStatus.UNAUTHORIZED) {
      throw new ApiRequestFatalError('Id token refresh rejected (401)');
    }

    if (result.networkError || result.status === 0) {
      throw new InternalError('Could not refresh auth token', {
        error: result.networkError,
        status: result.status,
      });
    }

    if (!result.ok) {
      throw new ApiRequestServerError(`Id token refresh failed with status code ${result.status}`);
    }

    if (!result.data?.token) {
      throw new InternalError('Empty response received from id token refresh');
    }

    return {
      token: result.data.token,
      refreshToken: result.data.refreshToken,
      refreshTokenExpiresAt: result.data.refreshTokenExpiresAt,
    };
  }

  public async refreshSession(refreshToken: string): Promise<RefreshTokenBundle> {
    const result = await authExchangeFetch<RefreshTokenBundle>({
      method: 'POST',
      path: '/auth/token',
      jsonBody: { refreshToken },
      bearerToken: this.store.idToken,
    });

    if (result.status === HttpStatus.UNAUTHORIZED) {
      throw new ApiRequestFatalError('Refresh token exchange rejected (401)');
    }

    if (result.networkError || result.status === 0) {
      throw new InternalError('Could not exchange refresh token', {
        error: result.networkError,
        status: result.status,
      });
    }

    if (!result.ok) {
      throw new ApiRequestServerError(
        `Refresh token exchange failed with status code ${result.status}`,
      );
    }

    if (!result.data?.token || !result.data.refreshToken) {
      throw new InternalError('Empty response received from refresh-token exchange');
    }

    return result.data;
  }

  public async revokeSession(refreshToken: string): Promise<void> {
    await authExchangeFetch({
      method: 'POST',
      path: '/auth/logout',
      jsonBody: { refreshToken },
      bearerToken: this.store.idToken,
    });
  }

  public async refreshIdToken(): Promise<string> {
    const result = await this.refreshIdTokenLegacy();
    return result.token;
  }

  public async getAccessTokenWithEmployerContext(employerId: number): Promise<string> {
    // NOTE: useFFRestApi grabs idToken from the auth service and uses it as the bearer token here
    const { data, error, statusCode } = await useFFRestApi(
      `/auth/access-token/employer/${employerId}`,
    )
      .get()
      .json<{ token: string }>();

    if (error.value) {
      // Only a definitive credential rejection is fatal. Other 4xx (429 rate
      // limiting, timeouts surfaced as 408) fall through to InternalError with
      // the status attached, so they stay retryable instead of forcing a
      // logout that would destroy a valid session.
      if (
        statusCode.value === HttpStatus.UNAUTHORIZED ||
        statusCode.value === HttpStatus.FORBIDDEN
      ) {
        throw new ApiRequestFatalError(
          `Request for access token failed with status code ${statusCode.value}`,
        );
      }
      if (statusCode.value && statusCode.value >= 500) {
        throw new ApiRequestServerError(
          `Request for access token failed with status code ${statusCode.value}`,
        );
      }

      throw new InternalError('Could not get access token', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }

    if (!data.value?.token) {
      throw new InternalError('Empty response received from access token request');
    }

    return data.value.token;
  }
}
